import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  useCarousel,
} from "@/components/ui/carousel";
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  CheckCircle2,
  TrendingUp,
  Clock,
  PoundSterling,
  CalendarCheck,
  MessageSquare,
  BarChart3,
  MousePointerClick,
  Target,
  ArrowRight,
  Smartphone,
  Users,
  MapPin,
  ArrowUp,
} from "lucide-react";

const CarouselIndicator = () => {
  const { api } = useCarousel();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  return (
    <div className="flex gap-2">
      {Array.from({ length: count }).map((_, i) => (
        <button
          key={i}
          onClick={() => api?.scrollTo(i)}
          className={`w-10 h-10 rounded-full flex items-center justify-center text-base font-bold transition-colors ${
            current === i + 1
              ? "bg-primary text-white shadow-md"
              : "bg-slate-100 text-slate-500 hover:bg-slate-200"
          }`}
        >
          {i + 1}
        </button>
      ))}
    </div>
  );
};

const Index = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }
  }, [location.hash]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background font-sans text-foreground">
      <SEOHead
        title="Window Cleaning Marketing Agency | Grow Your Rounds Fast"
        description="The UK's only window cleaning marketing agency with fully automated quoting and follow-up systems that turn enquiries into bookings."
        canonical="/"
        ogImage="https://vibe.filesafe.space/meta/1775166810156919871/og_image.png"
        schemaJson={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Clean Marketing",
          url: "https://www.cleanmarketing.co.uk",
          logo: "https://vibe.filesafe.space/1775166810156919871/attachments/07b67583-88fd-417b-bb44-aeaaee1d2dda.png",
          description:
            "The all-in-one marketing and automation platform built specifically for window cleaners in the UK.",
          contactPoint: {
            "@type": "ContactPoint",
            telephone: "01225 234456",
            contactType: "customer service",
            areaServed: "GB",
            availableLanguage: "en",
          },
        }}
      />
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[100svh] flex flex-col justify-center pt-16 pb-8 sm:pt-24 sm:pb-20 overflow-hidden bg-slate-50">
        <div className="absolute inset-0 z-0">
          <img
            src="https://vibe.filesafe.space/1775166810156919871/attachments/eb4534bf-7d5a-4dc0-b2f6-61b9ac5425a3.jpg"
            alt="Window cleaner at work"
            fetchPriority="high"
            loading="eager"
            width="1920"
            height="1080"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-white/40 bg-gradient-to-b from-white/60 via-white/40 to-white/70"></div>
        </div>
        <div className="container relative z-10 mx-auto px-4 text-center animate-fade-in">
          <div className="inline-flex items-center rounded-full border border-secondary/20 bg-white/60 px-3 py-1 text-xs sm:text-sm font-medium text-secondary mb-4 sm:mb-8 backdrop-blur-sm shadow-sm">
            <span className="flex h-2 w-2 rounded-full bg-primary mr-2"></span>
            Built by a window cleaner, for window cleaners - not by developers
          </div>

          <h1 className="mx-auto max-w-4xl text-3xl font-black tracking-tight sm:text-6xl md:text-7xl lg:text-8xl leading-[1.1]">
            <span className="text-secondary block drop-shadow-sm">
              Marketing
            </span>
            <span className="block mt-1 sm:mt-2">
              <span className="text-primary drop-shadow-sm">Made Easy</span>{" "}
              <span className="text-secondary drop-shadow-sm">For</span>
            </span>
            <span className="text-black block mt-1 sm:mt-2 drop-shadow-sm">
              Window Cleaners
            </span>
          </h1>

          <p className="mx-auto mt-2 sm:mt-6 max-w-2xl text-base font-medium text-slate-800 sm:text-2xl drop-shadow-sm">
            The UK’s Only Marketing Agency Built Exclusively for Window Cleaners
          </p>

          <p className="mx-auto mt-2 sm:mt-4 max-w-2xl text-sm text-slate-700 sm:text-xl drop-shadow-sm">
            Running a multi-van window cleaning company and struggling to keep
            your rounds full? We help ambitious window cleaners across the UK
            generate a consistent flow of high-quality leads using proven
            digital marketing systems. From Google Ads and automated bookings to
            relentless follow-up, we build a predictable growth engine so you
            can focus on the work, not chasing customers.
          </p>

          <div className="mt-4 sm:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
            <Button
              size="lg"
              className="text-base h-12 px-8 bg-primary text-primary-foreground hover:bg-[#e04825] transition-colors border-none shadow-md"
              asChild
            >
              <Link to="/book-demo">
                Book a Demo <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <span className="text-slate-600 font-medium">or</span>
            <Button
              size="lg"
              variant="outline"
              className="text-base h-12 px-8 border-secondary/30 bg-white/50 text-secondary hover:bg-secondary hover:text-white transition-colors backdrop-blur-sm shadow-sm"
              asChild
            >
              <a href="#pricing">See Pricing</a>
            </Button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section id="stats" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold tracking-tight text-secondary sm:text-4xl">
              Proven results across the UK & Ireland
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              We don't just promise growth. We deliver it.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            <Card className="border-secondary/10 shadow-md bg-white">
              <CardContent className="p-8 text-center flex flex-col items-center">
                <div className="p-4 bg-primary/10 rounded-full mb-6 text-primary">
                  <TrendingUp className="h-8 w-8" />
                </div>
                <h3 className="text-4xl font-extrabold text-secondary mb-2">
                  36,000+
                </h3>
                <p className="text-lg font-medium text-slate-700">
                  Leads Processed
                </p>
                <p className="text-sm text-slate-500 mt-2">
                  High-converting local campaigns generating consistent
                  inquiries.
                </p>
              </CardContent>
            </Card>

            <Card className="border-secondary/10 shadow-md bg-white">
              <CardContent className="p-8 text-center flex flex-col items-center">
                <div className="p-4 bg-primary/10 rounded-full mb-6 text-primary">
                  <CalendarCheck className="h-8 w-8" />
                </div>
                <h3 className="text-4xl font-extrabold text-secondary mb-2">
                  12,500+
                </h3>
                <p className="text-lg font-medium text-slate-700">
                  Bookings Made
                </p>
                <p className="text-sm text-slate-500 mt-2">
                  Fully automated jobs scheduled directly into our clients'
                  calendars.
                </p>
              </CardContent>
            </Card>

            <Card className="border-secondary/10 shadow-md bg-white">
              <CardContent className="p-8 text-center flex flex-col items-center">
                <div className="p-4 bg-secondary/10 rounded-full mb-6 text-secondary">
                  <PoundSterling className="h-8 w-8" />
                </div>
                <h3 className="text-4xl font-extrabold text-secondary mb-2">
                  £2.5M+
                </h3>
                <p className="text-lg font-medium text-slate-700">
                  Recurring Revenue Generated
                </p>
                <p className="text-sm text-slate-500 mt-2">
                  Added to our clients' businesses in the last 24 months.
                </p>
              </CardContent>
            </Card>

            <Card className="border-secondary/10 shadow-md bg-white">
              <CardContent className="p-8 text-center flex flex-col items-center">
                <div className="p-4 bg-secondary/10 rounded-full mb-6 text-secondary">
                  <Clock className="h-8 w-8" />
                </div>
                <h3 className="text-4xl font-extrabold text-secondary mb-2">
                  6,500+
                </h3>
                <p className="text-lg font-medium text-slate-700">
                  Admin Hours Saved
                </p>
                <p className="text-sm text-slate-500 mt-2">
                  Through automated quoting, booking, and follow-ups.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-secondary sm:text-4xl mb-4">
              Everything you need to scale.
            </h2>
            <p className="text-lg text-slate-600">
              We replace the headache of manual quotes and chasing leads with a
              streamlined, automated system designed specifically for window
              cleaners.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex flex-col p-6 rounded-2xl bg-white border border-slate-200 transition-all hover:shadow-md hover:border-primary/30"
              >
                <div className="h-12 w-12 rounded-lg bg-secondary/10 flex items-center justify-center text-secondary mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-secondary mb-3">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Advantage Section */}
      <section className="py-24 bg-white border-y border-slate-200">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-sm font-medium text-primary mb-6">
                Exclusive Insights
              </div>
              <h2 className="text-3xl font-bold tracking-tight text-secondary sm:text-4xl mb-6">
                The Ultimate Data Advantage
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                We use data that no other window cleaning company has in the UK.
                We know the UK-wide conversion rates and conversion rates for
                different types of properties (down to the number of bedrooms).
              </p>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                We help companies make data-driven decisions based on actual
                live data which updates in real-time. Make direct comparisons
                between national averages and your own company's performance.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  "UK-Wide Conversion Rates",
                  "Property Type Conversion Data",
                  "Real-Time Data Updates",
                  "National vs Local Comparisons",
                ].map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center bg-slate-50 p-3 rounded-lg border border-slate-100"
                  >
                    <BarChart3 className="h-5 w-5 text-primary mr-3 shrink-0" />
                    <span className="text-sm font-medium text-secondary">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-tr from-secondary/10 to-primary/10 rounded-3xl transform -rotate-3"></div>
              <Card className="relative bg-white border-slate-200 shadow-xl overflow-hidden">
                <CardHeader className="bg-slate-50 border-b border-slate-100 pb-4">
                  <CardTitle className="text-lg text-secondary flex items-center">
                    <TrendingUp className="w-5 h-5 mr-2 text-primary" />
                    Live Market Insights
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium text-slate-600">
                        Your Conversion Rate
                      </span>
                      <span className="font-bold text-secondary">34.2%</span>
                    </div>
                    <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-secondary w-[85%] rounded-full"></div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium text-slate-600">
                        National Average
                      </span>
                      <span className="font-bold text-slate-500">30.4%</span>
                    </div>
                    <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-slate-300 w-[75%] rounded-full"></div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Coverage Map Section */}
      <section
        id="coverage"
        className="py-24 bg-slate-50 border-b border-slate-200"
      >
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-12">
            <div className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-sm font-medium text-primary mb-6">
              <MapPin className="w-4 h-4 mr-2" /> Exclusive Territories
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-secondary sm:text-4xl mb-4">
              We only work with one cleaner per town. Ever.
            </h2>
            <div className="text-lg text-slate-600 max-w-3xl mx-auto space-y-6">
              <p>
                Unlike our competitors, we do not work with more than one person
                per town. (We know of one competitor running ads for 3 separate
                companies in 1 town!) We run campaigns on a per-town basis, not
                a radius. Some cities are split into separate towns based on how
                people actually search.
              </p>
              <div className="text-left bg-white p-6 rounded-2xl border border-slate-200 shadow-sm mx-auto max-w-2xl">
                <p className="font-bold text-secondary mb-4 text-center sm:text-left">
                  Why do we do this? Because running ads for multiple companies:
                </p>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mr-3" />
                    <span>
                      <strong>Dilutes the uniqueness of what we do.</strong>
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mr-3" />
                    <span>Pushes ad costs up for everyone.</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mr-3" />
                    <span>
                      Doesn't make you stand out from your competitors.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mr-3" />
                    <span>
                      Makes acquiring customers more expensive because you're
                      competing against someone with exactly the same setup and
                      advantage.
                    </span>
                  </li>
                </ul>
              </div>
              <p>
                Check if your areas are currently available, or join the
                waitlist if an area is already taken.
              </p>
            </div>
          </div>
          <div className="max-w-xl mx-auto flex justify-center">
            <Button
              size="lg"
              className="text-base h-14 px-10 bg-primary text-primary-foreground hover:bg-[#e04825] transition-colors shadow-md"
              asChild
            >
              <Link to="/availability">
                <MapPin className="mr-2 h-5 w-5" />
                Check If Your Area Is Available
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Target Audience / "Why Us" Section */}
      <section className="py-24 bg-secondary text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold sm:text-4xl mb-6">
                We know your business because we've run one.
              </h2>
              <p className="text-blue-100 text-lg mb-4 leading-relaxed">
                Clean Marketing was founded by James Savory, a window cleaner
                who spent over 7 years perfecting these exact systems in-house.{" "}
                <strong>
                  We built this entirely ourselves without a developer or any
                  developer experience - only based on our experience of running
                  a window cleaning business.
                </strong>
              </p>
              <p className="text-blue-100 text-lg mb-8 leading-relaxed">
                We manage over £70,000 in ad spend monthly, focusing purely on
                what works for window cleaning. We are a marketing agency not
                just a marketing system, and we are the only marketing agency
                who specialise in window cleaning in the UK.
              </p>
              <ul className="space-y-4">
                {[
                  "No generic PPC strategies - highly targeted local campaigns.",
                  "We know the difference between a search for 'window cleaning spray' and someone wanting a quote for their windows to be cleaned.",
                  "Regular marketing strategy meetings to plan for seasonality and growth.",
                  "Honest, transparent reporting. We don't hide behind vanity metrics.",
                ].map((item, i) => (
                  <li key={i} className="flex items-start">
                    <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mr-3" />
                    <span className="text-blue-50">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-tr from-primary to-primary/50 rounded-3xl transform rotate-3 opacity-20 blur-xl"></div>
              <Card className="relative bg-white text-slate-800 border-none shadow-xl">
                <CardHeader>
                  <CardTitle className="text-2xl text-secondary">
                    Who is this for?
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <h4 className="font-semibold text-secondary">
                      Ambitious Owners
                    </h4>
                    <p className="text-slate-600 text-sm">
                      You want to grow to multiple vans and understand that
                      marketing requires investment.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-semibold text-secondary">
                      Data-Driven
                    </h4>
                    <p className="text-slate-600 text-sm">
                      You want to see exactly where your £25 average Customer
                      Acquisition Cost is going.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-semibold text-secondary">
                      Ready to Scale
                    </h4>
                    <p className="text-slate-600 text-sm">
                      You're tired of being stuck on the tools and want systems
                      that generate bookings while you sleep.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section id="testimonials" className="py-24 bg-white overflow-hidden">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-16">
            <div className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-sm font-medium text-primary mb-6">
              Success Stories
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-secondary sm:text-4xl mb-4">
              Customer Testimonials
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Real results from window cleaning businesses across the UK.
            </p>
          </div>

          <Carousel className="w-full" opts={{ loop: true }}>
            <CarouselContent>
              {/* Lee Pryor */}
              <CarouselItem>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center px-2 md:px-12">
                  <div className="order-2 lg:order-1">
                    <h2 className="text-3xl font-bold tracking-tight text-secondary sm:text-4xl mb-6">
                      "In March we added £101,000 a year to our round."
                    </h2>
                    <p className="text-lg text-slate-600 mb-8">
                      We partner with ambitious companies like{" "}
                      <strong>Pryor's Window Cleaning</strong> - the largest
                      residential window cleaning company in the UK, based near
                      Gatwick.
                    </p>
                    <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 mb-8">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-slate-500 font-medium mb-1">
                            Ad Spend
                          </p>
                          <p className="text-3xl font-extrabold text-secondary">
                            £17,000
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-500 font-medium mb-1">
                            Added Revenue
                          </p>
                          <p className="text-3xl font-extrabold text-primary">
                            £101k
                            <span className="text-lg font-medium text-slate-500">
                              /yr
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="h-16 w-32 bg-white rounded-sm shadow-md flex items-center justify-center border border-slate-100 overflow-hidden">
                        <img
                          src="https://vibe.filesafe.space/1775166810156919871/attachments/477ac3d4-8e08-48ac-a1e2-ca9ecaf5ee94.png"
                          alt="Pryor's Window Cleaning Logo"
                          width="128"
                          height="64"
                          className="w-full h-full object-contain scale-[1.35]"
                        />
                      </div>
                      <div>
                        <p className="font-bold text-secondary text-lg">
                          Lee Pryor
                        </p>
                        <p className="text-sm text-slate-500">
                          Owner, Pryor's Window Cleaning
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="order-1 lg:order-2 relative">
                    <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-secondary/20 rounded-3xl transform rotate-3"></div>
                    <div className="relative rounded-3xl overflow-hidden shadow-xl bg-gradient-to-br from-secondary to-secondary/80 aspect-video flex flex-col items-center justify-center border border-slate-200">
                      <div className="h-16 w-16 rounded-full bg-white/20 flex items-center justify-center text-white mb-4 backdrop-blur-sm">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="w-8 h-8 ml-1"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4.5 5.653c0-1.427 1.529-2.33 2.779-1.643l11.54 6.347c1.295.712 1.295 2.573 0 3.286L7.28 19.99c-1.25.687-2.779-.217-2.779-1.643V5.653Z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <h3 className="text-2xl font-bold text-white tracking-tight">
                        Video Coming Soon
                      </h3>
                      <p className="text-white/80 mt-2 font-medium">
                        Lee's Success Story
                      </p>
                    </div>
                  </div>
                </div>
              </CarouselItem>

              {/* Robbi Whytock */}
              <CarouselItem>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center px-2 md:px-12">
                  <div className="order-2 lg:order-1">
                    <h2 className="text-3xl font-bold tracking-tight text-secondary sm:text-4xl mb-6">
                      "Reached a yearly round value of over £250,000."
                    </h2>
                    <p className="text-lg text-slate-600 mb-8">
                      We recently celebrated a massive milestone with{" "}
                      <strong>Robbi Whytock</strong>. By implementing our
                      targeted marketing systems, he's seen incredible growth in
                      just a few months.
                    </p>
                    <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 mb-8">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-slate-500 font-medium mb-1">
                            Added Since June
                          </p>
                          <p className="text-3xl font-extrabold text-primary">
                            £80k
                            <span className="text-lg font-medium text-slate-500">
                              /yr
                            </span>
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-500 font-medium mb-1">
                            Total Round Value
                          </p>
                          <p className="text-3xl font-extrabold text-secondary">
                            £250k+
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="h-16 w-32 bg-white rounded-sm shadow-md flex items-center justify-center border border-slate-100 p-3">
                        <img
                          src="https://vibe.filesafe.space/1775166810156919871/attachments/d862554c-fc11-4e98-8943-ff23500c3ab5.png"
                          alt="Legendary Window Cleaning Logo"
                          width="128"
                          height="64"
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div>
                        <p className="font-bold text-secondary text-lg">
                          Robbi Whytock
                        </p>
                        <p className="text-sm text-slate-500">
                          Legendary Window Cleaning, Luton
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="order-1 lg:order-2 relative">
                    <div className="absolute inset-0 bg-gradient-to-bl from-primary/20 to-secondary/20 rounded-3xl transform -rotate-3"></div>
                    <div className="relative rounded-3xl overflow-hidden shadow-xl border border-slate-200 aspect-[4/3] sm:aspect-video lg:aspect-auto lg:h-[400px]">
                      <img
                        src="https://vibe.filesafe.space/1775166810156919871/attachments/b147b7df-4634-47d7-8fc9-20402a0d2ddd.jpg"
                        alt="Robbi Whytock receiving his award"
                        loading="lazy"
                        width="800"
                        height="600"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                </div>
              </CarouselItem>

              {/* Will Farras */}
              <CarouselItem>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center px-2 md:px-12">
                  <div className="order-2 lg:order-1">
                    <h2 className="text-3xl font-bold tracking-tight text-secondary sm:text-4xl mb-6">
                      "Doubled our round to £160,000 and hired our first
                      employee."
                    </h2>
                    <p className="text-lg text-slate-600 mb-8">
                      <strong>Will Farras</strong> from Brighton-based Farras
                      Window Cleaning has experienced explosive growth, doubling
                      his business in just 6 months.
                    </p>
                    <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 mb-8">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-slate-500 font-medium mb-1">
                            Round in Sept 2025
                          </p>
                          <p className="text-3xl font-extrabold text-secondary">
                            £80k
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-500 font-medium mb-1">
                            Round in Mar 2026
                          </p>
                          <p className="text-3xl font-extrabold text-primary">
                            £160k
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="h-16 w-32 bg-white rounded-sm shadow-md flex items-center justify-center border border-slate-100 p-3">
                        <img
                          src="https://vibe.filesafe.space/1775166810156919871/attachments/abf2c69d-9faf-47e1-9eec-bedfb43b7d11.png"
                          alt="Farras Window Cleaning Logo"
                          width="128"
                          height="64"
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div>
                        <p className="font-bold text-secondary text-lg">
                          Will Farras
                        </p>
                        <p className="text-sm text-slate-500">
                          Farras Window Cleaning, Brighton
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="order-1 lg:order-2 relative">
                    <div className="absolute inset-0 bg-gradient-to-bl from-primary/20 to-secondary/20 rounded-3xl transform -rotate-3"></div>
                    <div className="relative rounded-3xl overflow-hidden shadow-xl border border-slate-200 aspect-[4/3] sm:aspect-video lg:aspect-auto lg:h-[400px]">
                      <img
                        src="https://vibe.filesafe.space/1775166810156919871/attachments/751503f6-bd4b-450a-ae95-5884cfeebbea.jpg"
                        alt="Will Farras"
                        loading="lazy"
                        width="800"
                        height="600"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                </div>
              </CarouselItem>
            </CarouselContent>

            <div className="hidden md:block">
              <CarouselPrevious className="absolute -left-4 lg:-left-12 top-1/2 -translate-y-1/2 h-14 w-14 rounded-full bg-primary text-white border-none hover:bg-[#e04825] hover:text-white shadow-lg z-10 opacity-90 hover:opacity-100" />
              <CarouselNext className="absolute -right-4 lg:-right-12 top-1/2 -translate-y-1/2 h-14 w-14 rounded-full bg-primary text-white border-none hover:bg-[#e04825] hover:text-white shadow-lg z-10 opacity-90 hover:opacity-100" />
            </div>
            <div className="flex items-center justify-center mt-6 md:mt-10">
              <CarouselIndicator />
            </div>
          </Carousel>
        </div>
      </section>

      {/* Podcast Section */}
      <section className="py-24 bg-slate-900 text-white overflow-hidden">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">
            <div className="w-full lg:w-1/2 space-y-6 lg:pr-8">
              <div className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
                Featured On
              </div>
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                Watch us on The Uncleanables Podcast
              </h2>
              <p className="text-lg text-slate-300">
                We recently sat down with The Uncleanables - a podcast we
                proudly sponsor and support - to discuss the exact marketing
                strategies that are working right now for window cleaners across
                the UK.
              </p>
              <div className="pt-4 flex flex-col sm:flex-row items-center sm:items-start gap-8">
                <img
                  src="https://vibe.filesafe.space/1775166810156919871/attachments/9f531d29-517d-40d4-8af9-b2e1fc27ebfa.webp"
                  alt="The Uncleanables Podcast"
                  loading="lazy"
                  width="280"
                  height="176"
                  className="h-36 sm:h-44 max-w-[280px] object-contain"
                />
                <div className="flex flex-col justify-center gap-3">
                  <Button
                    className="bg-[#ddab1b] text-white hover:bg-[#08aed8] hover:text-white border-none transition-colors"
                    asChild
                  >
                    <a
                      href="https://www.theuncleanables.co.uk"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Visit The Uncleanables{" "}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                  <div className="flex flex-col gap-2 mt-2">
                    <p className="text-sm text-slate-400">
                      Join their community for support:
                    </p>
                    <Button
                      className="bg-[#5865F2] hover:bg-[#4752C4] text-white transition-colors border-none"
                      asChild
                    >
                      <a
                        href="https://www.theuncleanables.co.uk/join"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <svg
                          role="img"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-5 h-5 mr-2 fill-current"
                        >
                          <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
                        </svg>
                        Join the Discord
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <div className="w-full lg:w-1/2">
              <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl border border-slate-700 bg-slate-800">
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src="https://www.youtube.com/embed/Fjt-nrmVbtU?si=XDrBT8rOYa8dn0zb"
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  loading="eager"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Coming Soon Section */}
      <section
        id="coming-soon"
        className="py-24 bg-white border-t border-slate-200"
      >
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-16">
            <div className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-sm font-medium text-primary mb-6">
              Future Updates
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-secondary sm:text-4xl">
              Coming Soon to Clean Marketing
            </h2>
            <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
              We're constantly building new tools to help you scale. Here's a
              sneak peek at what's launching next.
            </p>
          </div>

          {/* AI Receptionist */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-bold text-secondary mb-4">
                AI Receptionist
              </h3>
              <p className="text-lg text-slate-600 mb-6">
                The AI receptionist will be included for all Clean Marketing
                customers, but they will be given full control about what is
                controlled by a human and what AI features they want to use in
                their business. (Humans sell to humans, AI is there to assist
                you not replace you).
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-8">
                <div>
                  <h4 className="font-semibold text-secondary mb-3 flex items-center">
                    <Smartphone className="w-5 h-5 mr-2 text-primary" />{" "}
                    Capabilities
                  </h4>
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-start">
                      <CheckCircle2 className="w-4 h-4 text-primary mr-2 mt-0.5 shrink-0" />{" "}
                      Inbound Voice AI (give quotes and book people in over the
                      phone)
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-4 h-4 text-primary mr-2 mt-0.5 shrink-0" />{" "}
                      Outbound Voice AI (Phone all leads to encourage them to
                      book)
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-4 h-4 text-primary mr-2 mt-0.5 shrink-0" />{" "}
                      Website chat widgets
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-4 h-4 text-primary mr-2 mt-0.5 shrink-0" />{" "}
                      Answer questions via WhatsApp and confirm booking details
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-4 h-4 text-primary mr-2 mt-0.5 shrink-0" />{" "}
                      Respond to messages via SMS, WhatsApp, Email, Facebook
                      messenger etc
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-secondary mb-3 flex items-center">
                    <Target className="w-5 h-5 mr-2 text-primary" /> Benefits
                  </h4>
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-start">
                      <CheckCircle2 className="w-4 h-4 text-primary mr-2 mt-0.5 shrink-0" />{" "}
                      ZERO Missed Calls
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-4 h-4 text-primary mr-2 mt-0.5 shrink-0" />{" "}
                      Automate message replies
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-4 h-4 text-primary mr-2 mt-0.5 shrink-0" />{" "}
                      Available 24/7
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-4 h-4 text-primary mr-2 mt-0.5 shrink-0" />{" "}
                      Gives quotes & answers questions
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-4 h-4 text-primary mr-2 mt-0.5 shrink-0" />{" "}
                      Filters out unqualified leads
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-4 h-4 text-primary mr-2 mt-0.5 shrink-0" />{" "}
                      Instant lead engagement
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-4 h-4 text-primary mr-2 mt-0.5 shrink-0" />{" "}
                      Reduces admin workload
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="relative aspect-video lg:aspect-square rounded-3xl overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center">
              <img
                src="https://vibe.filesafe.space/1775166810156919871/assets/3b8f1fc3-2df1-4ea9-bcc2-eade59243eae.png"
                alt="AI Receptionist"
                loading="lazy"
                width="600"
                height="600"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Customers Carousel */}
      <section className="py-16 bg-white border-t border-slate-200 overflow-hidden">
        <div className="container mx-auto px-4 max-w-6xl">
          <p className="text-center text-sm font-semibold text-slate-500 uppercase tracking-wider mb-10">
            Trusted by growing window cleaning businesses across the UK
          </p>
          <div className="flex overflow-hidden relative w-full before:absolute before:left-0 before:top-0 before:z-10 before:h-full before:w-16 before:bg-gradient-to-r before:from-white before:to-transparent after:absolute after:right-0 after:top-0 after:z-10 after:h-full after:w-16 after:bg-gradient-to-l after:from-white after:to-transparent">
            <div className="flex shrink-0 animate-marquee whitespace-nowrap">
              {[
                "https://vibe.filesafe.space/1775166810156919871/attachments/5f55d439-93d2-48ce-97d2-97d772f6b2b4.png",
                "https://vibe.filesafe.space/1775166810156919871/attachments/71d8df8f-61f7-42f8-af06-e08cc7dc5c4f.png",
                "https://vibe.filesafe.space/1775166810156919871/attachments/ff5573b4-9092-434e-aa94-7d152f65bf91.png",
                "https://vibe.filesafe.space/1775166810156919871/attachments/4d51dc3c-3056-4722-9e7a-47696a047d6e.png",
                "https://vibe.filesafe.space/1775166810156919871/attachments/3df08650-1b31-4d0e-bfb1-75c30fa259f3.png",
                "https://vibe.filesafe.space/1775166810156919871/attachments/477ac3d4-8e08-48ac-a1e2-ca9ecaf5ee94.png",
                "https://vibe.filesafe.space/1775166810156919871/attachments/d862554c-fc11-4e98-8943-ff23500c3ab5.png",
                "https://vibe.filesafe.space/1775166810156919871/attachments/abf2c69d-9faf-47e1-9eec-bedfb43b7d11.png",
              ].map((src, index) => (
                <div
                  key={index}
                  className="flex-none w-48 md:w-64 mx-8 h-32 flex items-center justify-center"
                >
                  <img
                    src={src}
                    alt={`Customer logo ${index + 1}`}
                    loading="lazy"
                    width="256"
                    height="128"
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
              ))}
            </div>
            <div
              className="flex shrink-0 animate-marquee whitespace-nowrap"
              aria-hidden="true"
            >
              {[
                "https://vibe.filesafe.space/1775166810156919871/attachments/5f55d439-93d2-48ce-97d2-97d772f6b2b4.png",
                "https://vibe.filesafe.space/1775166810156919871/attachments/71d8df8f-61f7-42f8-af06-e08cc7dc5c4f.png",
                "https://vibe.filesafe.space/1775166810156919871/attachments/ff5573b4-9092-434e-aa94-7d152f65bf91.png",
                "https://vibe.filesafe.space/1775166810156919871/attachments/4d51dc3c-3056-4722-9e7a-47696a047d6e.png",
                "https://vibe.filesafe.space/1775166810156919871/attachments/3df08650-1b31-4d0e-bfb1-75c30fa259f3.png",
                "https://vibe.filesafe.space/1775166810156919871/attachments/477ac3d4-8e08-48ac-a1e2-ca9ecaf5ee94.png",
                "https://vibe.filesafe.space/1775166810156919871/attachments/d862554c-fc11-4e98-8943-ff23500c3ab5.png",
                "https://vibe.filesafe.space/1775166810156919871/attachments/abf2c69d-9faf-47e1-9eec-bedfb43b7d11.png",
              ].map((src, index) => (
                <div
                  key={index}
                  className="flex-none w-48 md:w-64 mx-8 h-32 flex items-center justify-center"
                >
                  <img
                    src={src}
                    alt={`Customer logo ${index + 1}`}
                    loading="lazy"
                    width="256"
                    height="128"
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-16">
            <div className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-sm font-medium text-primary mb-6">
              Got Questions?
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-secondary sm:text-4xl">
              Frequently Asked Questions
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Everything you need to know about Clean Marketing.
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full space-y-4">
            <AccordionItem
              value="item-1"
              className="bg-slate-50 border border-slate-200 rounded-xl px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-secondary hover:no-underline hover:text-primary transition-colors py-6">
                Do you work with multiple window cleaners in the same area?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 text-base leading-relaxed pb-6">
                No. We offer exclusive territories and only work with one window
                cleaner per town. You can check our coverage map to see if your
                area is available. We do this to ensure we aren't competing
                against ourselves and can deliver the best possible results for
                your business.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="item-2"
              className="bg-slate-50 border border-slate-200 rounded-xl px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-secondary hover:no-underline hover:text-primary transition-colors py-6">
                Do I have to manually quote every new lead?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 text-base leading-relaxed pb-6">
                Not anymore! Our system includes an automated quoting and
                booking form that calculates prices based on house type, number
                of bedrooms, extensions, and conservatories. Customers can also
                add extra services like gutter clearance, internal window
                cleaning, and conservatory roof cleaning to get their final
                price and book directly into your schedule without you lifting a
                finger.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="item-3"
              className="bg-slate-50 border border-slate-200 rounded-xl px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-secondary hover:no-underline hover:text-primary transition-colors py-6">
                What happens if a lead doesn't book immediately?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 text-base leading-relaxed pb-6">
                We know that 98% of WhatsApps are read. Our system automatically
                follows up with unconverted leads via WhatsApp and email for
                days, chasing them so you don't lose the sale. This automated
                follow-up significantly increases your overall conversion rate.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="item-4"
              className="bg-slate-50 border border-slate-200 rounded-xl px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-secondary hover:no-underline hover:text-primary transition-colors py-6">
                Do you use generic marketing templates?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 text-base leading-relaxed pb-6">
                Never. Clean Marketing was built by a window cleaner who spent 7
                years perfecting these systems. We use highly-targeted local
                Google and Facebook ads specifically designed for window
                cleaners, not generic PPC strategies. We know the difference
                between someone searching for "window cleaning spray" and
                someone wanting a quote.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="item-5"
              className="bg-slate-50 border border-slate-200 rounded-xl px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-secondary hover:no-underline hover:text-primary transition-colors py-6">
                What is included in the monthly fee?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 text-base leading-relaxed pb-6">
                The Growth System includes full Google & Facebook Ads
                management, custom dedicated landing pages, our automated
                quoting/booking system, CRM access, review generation,
                WhatsApp/Email follow-ups, and targeting for up to 7 towns.
                Additional towns are available for a small extra fee.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="item-6"
              className="bg-slate-50 border border-slate-200 rounded-xl px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-secondary hover:no-underline hover:text-primary transition-colors py-6">
                How do I know if my campaigns are actually working?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 text-base leading-relaxed pb-6">
                We believe in total transparency. You get full access to a
                financial dashboard where you can track leads, calls, bookings,
                first-clean value, and recurring revenue added in real-time. We
                also hold regular strategy meetings to plan for seasonality and
                growth.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="item-7"
              className="bg-slate-50 border border-slate-200 rounded-xl px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-secondary hover:no-underline hover:text-primary transition-colors py-6">
                Is there a minimum contract term?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 text-base leading-relaxed pb-6">
                Yes, we have a minimum contract term of 3 months. This is to
                give the ads the best chance to establish and to see if we work
                well together in growing your business.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="item-8"
              className="bg-slate-50 border border-slate-200 rounded-xl px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-secondary hover:no-underline hover:text-primary transition-colors py-6">
                Do you guarantee a certain number of leads or bookings?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 text-base leading-relaxed pb-6">
                Due to the dynamic nature of digital marketing, we can never
                guarantee an exact volume of leads or bookings. However, our
                track record speaks for itself. We've generated over £2.5m+ in
                recurring revenue for our clients, and our data shows that our
                highly targeted campaigns typically start delivering
                high-quality inquiries from day one. We don't rely on
                guesswork—we rely on proven, data-driven systems that work.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="item-9"
              className="bg-slate-50 border border-slate-200 rounded-xl px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-secondary hover:no-underline hover:text-primary transition-colors py-6">
                Is ad spend included in the monthly fee?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 text-base leading-relaxed pb-6">
                No, the monthly management fee does not include ad spend. You
                will pay Google and Facebook directly for your ads. We require a
                minimum ad spend of £1,000 per month. This ensures your
                campaigns have enough budget to compete effectively in your
                local area and generate a meaningful return on investment.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Pricing */}
      <section
        id="pricing"
        className="py-24 bg-slate-50 border-t border-slate-200"
      >
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-secondary sm:text-4xl">
              Simple, transparent pricing.
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Everything you need to dominate your local area.
            </p>
          </div>

          <div className="mx-auto max-w-lg">
            <Card className="border-primary shadow-xl relative overflow-hidden bg-white">
              <CardHeader className="text-center pb-8 pt-10">
                <CardTitle className="text-2xl mb-2 text-secondary">
                  The Growth System
                </CardTitle>
                <CardDescription className="text-slate-500">
                  Full marketing & automation suite
                </CardDescription>
                <div className="mt-6 flex justify-center items-baseline text-4xl sm:text-5xl font-extrabold text-secondary">
                  <span className="text-xl font-medium text-slate-500 mr-2">
                    From
                  </span>
                  £749
                  <span className="text-xl font-medium text-slate-500 ml-1">
                    /mo +VAT
                  </span>
                </div>
                <div className="mt-2 space-y-1">
                  <p className="text-sm text-slate-500">
                    + £900 one-time setup fee
                  </p>
                  <p className="text-sm font-bold text-primary">
                    Plus your ad spend (Min. £1,000/mo)
                  </p>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-4 mb-8">
                  {[
                    "Google Ads & Facebook Ads Management",
                    "Custom Dedicated Landing Pages",
                    "Automated Online Quoting & Booking System",
                    "Target up to 7 towns included",
                    "WhatsApp & Email Follow-up Sequences",
                    "Review Generation System",
                    "Full CRM Access & Financial Dashboard",
                    "Regular marketing strategy meetings",
                  ].map((feature, i) => (
                    <li key={i} className="flex items-center">
                      <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mr-3" />
                      <span className="text-sm text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="text-xs text-center text-slate-500 mb-6">
                  Additional towns available at £70 per month per additional
                  town.
                </div>
                <div className="flex flex-col gap-3">
                  <Button
                    className="w-full h-12 text-base bg-primary text-primary-foreground hover:bg-[#e04825] transition-colors"
                    asChild
                  >
                    <Link to="/book-demo">Book a Demo</Link>
                  </Button>
                  <div className="text-center text-sm text-slate-500 font-medium my-1">
                    or
                  </div>
                  <Button
                    variant="outline"
                    className="w-full h-12 text-base border-secondary bg-transparent text-secondary hover:bg-secondary hover:text-white transition-colors"
                    asChild
                  >
                    <Link to="/signup">Sign Up Now</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary text-white text-center">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold sm:text-4xl mb-6">
            Ready to stop chasing leads manually?
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Join over 45 window cleaning businesses across the UK and Ireland
            that have transformed their growth with Clean Marketing.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              size="lg"
              variant="secondary"
              className="text-lg h-14 px-10 bg-white text-primary hover:bg-slate-100 hover:text-[#e04825] transition-colors"
              asChild
            >
              <Link to="/book-demo">Book a Demo</Link>
            </Button>
            <span className="text-white/80 font-medium text-lg">or</span>
            <Button
              size="lg"
              variant="outline"
              className="text-lg h-14 px-10 border-white bg-transparent text-white hover:bg-secondary hover:text-white hover:border-secondary transition-colors"
              asChild
            >
              <Link to="/signup">Sign Up Now</Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />

      {/* Scroll to top */}
      {showScrollTop && (
        <Button
          size="icon"
          className="fixed bottom-6 right-6 z-50 rounded-xl bg-primary text-white shadow-lg hover:bg-[#e04825] transition-all duration-300 animate-fade-in"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        >
          <ArrowUp className="h-5 w-5" />
          <span className="sr-only">Scroll to top</span>
        </Button>
      )}
    </div>
  );
};

const features = [
  {
    icon: <CalendarCheck />,
    title: "Automated Quoting & Booking",
    description:
      "Our highly-optimized form calculates prices based on house type, number of bedrooms, extensions, and conservatories. Customers can also add extra services like gutter clearance or internal cleaning and book directly online.",
  },
  {
    icon: <MousePointerClick />,
    title: "Google Ads Management",
    description:
      "Hyper-local, highly targeted search ads that capture people actively looking for a window cleaner right now. We manage the bidding to get you the lowest cost per lead.",
  },
  {
    icon: <Target />,
    title: "Facebook Ads Management",
    description:
      "High-converting social campaigns with proven creatives and copy. We manage the targeting and optimization to generate consistent, high-quality local inquiries.",
  },
  {
    icon: <MessageSquare />,
    title: "WhatsApp & Email Follow-ups",
    description:
      "98% of WhatsApps are read. We automatically follow up with leads who don't book immediately, chasing them for days so you don't lose the sale.",
  },
  {
    icon: <BarChart3 />,
    title: "Financial Dashboard & CRM",
    description:
      "See exactly how many leads, calls, and bookings you have. Track your first-clean value and recurring revenue added, all in one easy-to-read dashboard.",
  },
  {
    icon: <CheckCircle2 />,
    title: "Review Generation",
    description:
      "Automatically ask for reviews after the first clean. Positive reviews go straight to Google; negative feedback comes to you privately to resolve.",
  },
];

export default Index;
